import { z } from "zod";

import { TIME_PERIOD_PRESETS } from "../../../../shared/domain/dashboard/time-period.constants";

/**
 * GET /api/dashboard
 *
 * Get organization dashboard metrics for a time period.
 */

export const GetDashboardDataQuerySchema = z.object({
  selectedTimePeriod: z.enum(TIME_PERIOD_PRESETS),
  deploymentId: z.string().uuid().optional()
});

// Time series data point
const TimeSeriesDataPointSchema = z.object({
  date: z.string(),
  value: z.number()
});

// Breakdown data point
const BreakdownDataPointSchema = z.object({
  entityId: z.string(),
  value: z.number()
});

// Total cost structure
const TotalCostSchema = z.object({
  _sum: z.object({
    priceInUSDTenThousandths: z.number().nullable()
  })
});

// Deployment metadata. `deletedAt` is non-null for tombstones AND for
// soft-deleted active rows — present so the dashboard can render them in
// breakdowns while filtering them out of dropdown selectors.
const DeploymentMetadataSchema = z.object({
  deploymentId: z.string(),
  deploymentName: z.string(),
  deploymentType: z.string(),
  agentId: z.string().nullable(),
  deletedAt: z.coerce.date().nullable()
});

// Agent metadata. Same deletedAt semantics as DeploymentMetadataSchema.
const AgentMetadataSchema = z.object({
  id: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  profile_picture: z.string().nullable(),
  deletedAt: z.coerce.date().nullable()
});

// Per-framework satisfaction aggregate. `framework` carries the
// SatisfactionFramework enum value as a string so the schema stays
// independent of the Prisma client at the API boundary.
const SatisfactionFrameworkAggregateSchema = z.object({
  framework: z.string(),
  totalCount: z.number(),
  averageRawScore: z.number()
});

// Per-entity (agent / deployment) satisfaction breakdown split by
// framework so values stay comparable across frameworks with
// different scales.
const SatisfactionBreakdownDataPointSchema = z.object({
  entityId: z.string(),
  framework: z.string(),
  totalCount: z.number(),
  averageRawScore: z.number()
});

// Per-(framework, day) satisfaction trend. Rendered as one line per
// framework on the chart so each line stays on its native scale.
const SatisfactionFrameworkTimeSeriesPointSchema = z.object({
  framework: z.string(),
  date: z.string(),
  averageRawScore: z.number(),
  totalCount: z.number()
});

export const GetDashboardDataResponseSchema = z.object({
  // Totals
  totalConversations: z.number(),
  totalConversationsPreviousPeriod: z.number(),
  totalMessages: z.number(),
  totalMessagesPreviousPeriod: z.number(),
  totalUniqueUsers: z.number(),
  totalUniqueUsersPreviousPeriod: z.number(),
  totalTokens: TotalCostSchema,
  totalTokensPreviousPeriod: TotalCostSchema,

  // Percentage changes
  totalConversationsPreviousPeriodPercentageDiff: z.number(),
  totalMessagesPreviousPeriodPercentageDiff: z.number(),
  totalUniqueUsersPreviousPeriodPercentageDiff: z.number(),
  totalTokensPreviousPeriodPercentageDiff: z.number(),

  // Time series
  totalCostPerDay: z.array(TimeSeriesDataPointSchema),
  totalCostPerDayPreviousPeriod: z.array(TimeSeriesDataPointSchema),
  totalConversationsPerDay: z.array(TimeSeriesDataPointSchema),
  totalConversationsPerDayPreviousPeriod: z.array(TimeSeriesDataPointSchema),
  totalUniqueUsersPerDay: z.array(TimeSeriesDataPointSchema),
  totalUniqueUsersPerDayPreviousPeriod: z.array(TimeSeriesDataPointSchema),
  totalMessagesPerDay: z.array(TimeSeriesDataPointSchema),
  totalMessagesPerDayPreviousPeriod: z.array(TimeSeriesDataPointSchema),
  numberOfScoresEqualTo1PerDay: z.array(TimeSeriesDataPointSchema),
  numberOfScoresEqualTo1PerDayPreviousPeriod: z.array(TimeSeriesDataPointSchema),
  numberOfScoresEqualTo0PerDay: z.array(TimeSeriesDataPointSchema),
  numberOfScoresEqualTo0PerDayPreviousPeriod: z.array(TimeSeriesDataPointSchema),

  // Deployment breakdowns
  breakdownDeploymentSessionsPerDeploymentId: z.array(BreakdownDataPointSchema),
  breakdownMessagesPerDeploymentId: z.array(BreakdownDataPointSchema),
  breakdownConversationsPerDeploymentId: z.array(BreakdownDataPointSchema),
  breakdownSatisfactionScoresEqualTo1PerDeploymentId: z.array(BreakdownDataPointSchema),
  breakdownSatisfactionScoresEqualTo0PerDeploymentId: z.array(BreakdownDataPointSchema),

  // Channel breakdowns (cost, conversations, messages per channel type)
  breakdownCostByChannel: z.array(BreakdownDataPointSchema),
  breakdownConversationsByChannel: z.array(BreakdownDataPointSchema),
  breakdownMessagesByChannel: z.array(BreakdownDataPointSchema),

  // Cost breakdowns (per model, per deployment)
  breakdownCostByModel: z.array(BreakdownDataPointSchema),
  breakdownCostByDeployment: z.array(BreakdownDataPointSchema),

  // Token split (input vs output)
  tokenSplit: z.object({
    inputTokens: z.number(),
    outputTokens: z.number()
  }),

  // Unique users breakdowns
  breakdownUniqueUsersPerDeploymentId: z.array(BreakdownDataPointSchema),
  breakdownUniqueUsersPerAgentId: z.array(BreakdownDataPointSchema),

  // Agent breakdowns
  breakDownRatings1PerAgent: z.array(BreakdownDataPointSchema),
  breakDownRatings1PerAgentPreviousPeriod: z.array(BreakdownDataPointSchema),
  breakDownRatings0PerAgent: z.array(BreakdownDataPointSchema),
  breakDownRatings0PerAgentPreviousPeriod: z.array(BreakdownDataPointSchema),
  breakDownConversationsPerAgent: z.array(BreakdownDataPointSchema),
  breakDownConversationsPerAgentPreviousPeriod: z.array(BreakdownDataPointSchema),
  breakdownMessagesPerAgent: z.array(BreakdownDataPointSchema),
  breakdownMessagesPerAgentPreviousPeriod: z.array(BreakdownDataPointSchema),

  // Metadata
  allAgentsOfOrganization: z.array(AgentMetadataSchema),
  allDeploymentsOfOrganization: z.array(DeploymentMetadataSchema),

  // Satisfaction framework analytics
  satisfactionAggregatesByFramework: z.array(SatisfactionFrameworkAggregateSchema),
  satisfactionAggregatesByFrameworkPreviousPeriod: z.array(SatisfactionFrameworkAggregateSchema),
  satisfactionAverageScorePerDay: z.array(SatisfactionFrameworkTimeSeriesPointSchema),
  satisfactionAverageScoreByAgent: z.array(SatisfactionBreakdownDataPointSchema),
  satisfactionAverageScoreByDeployment: z.array(SatisfactionBreakdownDataPointSchema)
});
